// Contact : le formulaire accepté du prototype, branché sur la fonction serveur
// sendContact (courriel à info@tg3r.com). Validation native au blur et à
// l'envoi, résumé d'erreurs focalisable, région de statut, bouton aria-busy.
import { createSignal, For, Show } from 'solid-js';
import { Meta, Title } from '@solidjs/meta';
import { useReveal } from '~/lib/mount';
import { sendContact, type ContactPayload } from '~/server/contact';

type FieldEl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
interface Err {
  cause: string;
  full: string;
}

const BUSY = 'Envoi en cours…';
const IDLE = 'Envoyer ma demande';
const SUCCESS = 'Merci ! Votre demande a été reçue. Notre équipe vous contactera rapidement.';

/* Message = cause + solution (data-hint pour un champ vide, data-hint-format
   pour un format invalide), comme dans le prototype. */
function messageFor(field: FieldEl): Err {
  const lab = document.querySelector(`label[for="${field.id}"]`);
  const label = (lab?.textContent ?? field.name)
    .replace(/\*/g, '')
    .replace(/\s*\(facultatif\)/i, '')
    .trim();
  const v = field.validity;
  const hint = field.getAttribute('data-hint');
  const hintFormat = field.getAttribute('data-hint-format');
  let cause: string, fix: string;
  if (v.valueMissing) {
    cause = `${label} : ce champ est vide.`;
    fix = hint ?? 'Remplissez ce champ pour continuer.';
  } else if (v.typeMismatch && (field as HTMLInputElement).type === 'email') {
    cause = `${label} : l’adresse n’est pas valide.`;
    fix = hintFormat ?? 'Utilisez le format nom@entreprise.com.';
  } else if (v.patternMismatch) {
    cause = `${label} : le format n’est pas reconnu.`;
    fix = hintFormat ?? 'Vérifiez le format demandé sous le champ.';
  } else {
    cause = `${label} : la valeur n’est pas valide.`;
    fix = hintFormat ?? 'Corrigez la valeur pour continuer.';
  }
  return { cause, full: `${cause} ${fix}` };
}

export default function Contact() {
  useReveal();
  let formEl: HTMLFormElement | undefined;
  let summaryEl: HTMLDivElement | undefined;
  let statusEl: HTMLDivElement | undefined;
  const [errors, setErrors] = createSignal<Record<string, Err>>({});
  const [busy, setBusy] = createSignal(false);
  const [status, setStatus] = createSignal<{ ok: boolean; text: string } | null>(null);

  const fields = () =>
    [...(formEl?.querySelectorAll<FieldEl>('input, select, textarea') ?? [])].filter(
      (f) => !['hidden', 'submit', 'button'].includes((f as HTMLInputElement).type),
    );
  const setErr = (id: string, err: Err | null) =>
    setErrors((e) => {
      const n = { ...e };
      if (err) n[id] = err;
      else delete n[id];
      return n;
    });
  const validate = (f: FieldEl) => {
    const err = f.checkValidity() ? null : messageFor(f);
    setErr(f.id, err);
    return err;
  };
  const onBlur = (e: FocusEvent) => {
    const f = e.currentTarget as FieldEl;
    if (f.value !== '' || errors()[f.id]) validate(f);
  };
  const onInput = (e: InputEvent) => {
    const f = e.currentTarget as FieldEl;
    if (errors()[f.id] && f.checkValidity()) setErr(f.id, null);
  };
  const described = (id: string, help?: string) =>
    [help, errors()[id] ? `${id}-error` : undefined].filter(Boolean).join(' ') || undefined;
  const errList = () => Object.entries(errors());

  const submit = async (ev: SubmitEvent) => {
    ev.preventDefault();
    if (busy() || !formEl) return;
    setStatus(null);
    const bad = fields().filter((f) => validate(f) !== null);
    if (bad.length) {
      queueMicrotask(() => summaryEl?.focus());
      return;
    }
    setBusy(true);
    const data = Object.fromEntries(new FormData(formEl)) as Record<string, string>;
    try {
      await sendContact(data as unknown as ContactPayload);
      setStatus({ ok: true, text: SUCCESS });
      formEl.reset();
      setErrors({});
    } catch (e) {
      setStatus({
        ok: false,
        text: e instanceof Error && e.message ? e.message : "L'envoi a échoué. Réessayez ou écrivez-nous à info@tg3r.com.",
      });
    } finally {
      setBusy(false);
      queueMicrotask(() => statusEl?.focus());
    }
  };

  const FieldError = (p: { id: string }) => (
    <Show when={errors()[p.id]}>
      {(err) => (
        <p class="field-error is-visible" id={`${p.id}-error`}>
          <svg class="ico" aria-hidden="true">
            <use href="#i-alert" />
          </svg>
          <span>{err().full}</span>
        </p>
      )}
    </Show>
  );
  const invalid = (id: string) => (errors()[id] ? 'true' : 'false');

  return (
    <>
      <Title>Contact — Devenir partenaire ou client de TG3R</Title>
      <Meta
        name="description"
        content="Fabricant, détaillant ou restaurateur : écrivez à TG3R pour présenter vos produits ou ouvrir un compte. Formulaire et coordonnées."
      />

      <section class="hero-short" aria-labelledby="page-title">
        <div class="container">
          <p class="eyebrow">Contact</p>
          <h1 class="h1" id="page-title">
            Collaborons pour aller plus loin
          </h1>
          <p class="lead">
            Parlez-nous de vos produits ou de vos besoins d'approvisionnement : notre équipe vous
            répond rapidement.
          </p>
        </div>
      </section>

      <section class="section section--tight" aria-labelledby="form-title">
        <div class="container contact-grid">
          <div class="form-card reveal" id="devenir-partenaire">
            <p class="eyebrow">Écrivez-nous</p>
            <h2 class="h3" id="form-title">
              Dites-nous qui vous êtes et ce que vous cherchez
            </h2>
            <p class="form-note">Tous les champs sont obligatoires sauf le message.</p>

            <form
              class="form"
              id="contact-form"
              method="post"
              novalidate
              aria-labelledby="form-title"
              onSubmit={submit}
              ref={formEl}
            >
              <div
                class={errList().length ? 'form-summary is-visible' : 'form-summary'}
                id="form-summary"
                role="alert"
                tabindex="-1"
                aria-labelledby="form-summary-title"
                ref={summaryEl}
              >
                <Show when={errList().length > 0}>
                  <p class="summary-head" id="form-summary-title">
                    <svg class="ico" aria-hidden="true">
                      <use href="#i-alert" />
                    </svg>
                    <span>
                      {errList().length === 1
                        ? 'Il y a 1 erreur dans le formulaire'
                        : `Il y a ${errList().length} erreurs dans le formulaire`}
                    </span>
                  </p>
                  <ul>
                    <For each={errList()}>
                      {([id, err]) => (
                        <li>
                          <a
                            href={`#${id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById(id)?.focus();
                            }}
                          >
                            {err.cause}
                          </a>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>
              </div>
              <div
                class={status() ? 'form-status is-visible' : 'form-status'}
                id="form-status"
                role="status"
                tabindex="-1"
                ref={statusEl}
              >
                <Show when={status()}>
                  {(s) => (
                    <>
                      <svg class="ico" aria-hidden="true">
                        <use href={s().ok ? '#i-check' : '#i-alert'} />
                      </svg>
                      <span>{s().text}</span>
                    </>
                  )}
                </Show>
              </div>

              {/* Champ piège pour les robots : caché, jamais rempli par une personne */}
              <div class="sr-only" aria-hidden="true">
                <label for="website">Site web</label>
                <input id="website" name="website" type="text" tabindex="-1" autocomplete="off" />
              </div>

              <div class="field">
                <label for="type">Je suis</label>
                <select
                  id="type"
                  name="type"
                  required
                  autocomplete="off"
                  data-hint="Choisissez le profil qui vous décrit le mieux."
                  aria-invalid={invalid('type')}
                  aria-describedby={described('type')}
                  onBlur={onBlur}
                  onInput={onInput}
                >
                  <option value="">Sélectionnez une option</option>
                  <option value="fabricant">Fabricant ou transformateur</option>
                  <option value="detaillant">Détaillant</option>
                  <option value="restaurateur">Restaurateur</option>
                </select>
                <FieldError id="type" />
              </div>

              <div class="form-row">
                <div class="field">
                  <label for="entreprise">Nom de l'entreprise</label>
                  <input
                    id="entreprise"
                    name="entreprise"
                    type="text"
                    required
                    autocomplete="organization"
                    data-hint="Entrez le nom de votre entreprise."
                    aria-invalid={invalid('entreprise')}
                    aria-describedby={described('entreprise')}
                    onBlur={onBlur}
                    onInput={onInput}
                  />
                  <FieldError id="entreprise" />
                </div>
                <div class="field">
                  <label for="contact">Nom du contact</label>
                  <input
                    id="contact"
                    name="contact"
                    type="text"
                    required
                    autocomplete="name"
                    data-hint="Entrez le nom de la personne à joindre."
                    aria-invalid={invalid('contact')}
                    aria-describedby={described('contact')}
                    onBlur={onBlur}
                    onInput={onInput}
                  />
                  <FieldError id="contact" />
                </div>
              </div>

              <div class="form-row">
                <div class="field">
                  <label for="telephone">Téléphone</label>
                  <input
                    id="telephone"
                    name="telephone"
                    type="tel"
                    required
                    autocomplete="tel"
                    inputmode="tel"
                    pattern="[0-9\(\)\+\.\s\-]{10,20}"
                    data-hint="Entrez un numéro où vous joindre."
                    data-hint-format="Utilisez le format 514 555-0123."
                    aria-invalid={invalid('telephone')}
                    aria-describedby={described('telephone', 'telephone-help')}
                    onBlur={onBlur}
                    onInput={onInput}
                  />
                  <p class="field-help" id="telephone-help">
                    Format : 514 555-0123
                  </p>
                  <FieldError id="telephone" />
                </div>
                <div class="field">
                  <label for="courriel">Courriel</label>
                  <input
                    id="courriel"
                    name="courriel"
                    type="email"
                    required
                    autocomplete="email"
                    autocapitalize="none"
                    spellcheck={false}
                    inputmode="email"
                    data-hint="Entrez votre adresse courriel."
                    data-hint-format="Utilisez le format nom@entreprise.com."
                    aria-invalid={invalid('courriel')}
                    aria-describedby={described('courriel')}
                    onBlur={onBlur}
                    onInput={onInput}
                  />
                  <FieldError id="courriel" />
                </div>
              </div>

              <div class="field">
                <label for="produits">Produits / marques représentés</label>
                <textarea
                  id="produits"
                  name="produits"
                  rows="3"
                  required
                  data-hint="Nommez vos produits ou marques, même brièvement."
                  aria-invalid={invalid('produits')}
                  aria-describedby={described('produits', 'produits-help')}
                  onBlur={onBlur}
                  onInput={onInput}
                />
                <p class="field-help" id="produits-help">
                  Ex. : charcuteries, fromages, boissons
                </p>
                <FieldError id="produits" />
              </div>

              <div class="field">
                <label for="message">Message (facultatif)</label>
                <textarea id="message" name="message" rows="4" />
              </div>

              <div class="form-actions">
                <button
                  class="btn btn-primary"
                  type="submit"
                  aria-busy={busy() ? 'true' : undefined}
                  aria-disabled={busy() ? 'true' : undefined}
                >
                  <span class="btn-label">{busy() ? BUSY : IDLE}</span>
                  <span class="btn-disc" aria-hidden="true">
                    <svg class="ico">
                      <use href="#i-arrow" />
                    </svg>
                  </span>
                </button>
                <p class="form-consent">
                  En envoyant ce formulaire, vous consentez à ce que TG3R conserve vos renseignements
                  pour traiter votre demande, conformément à la Loi 25. Consultez notre{' '}
                  <a href="/confidentialite">politique de confidentialité</a>.
                </p>
              </div>
            </form>
          </div>

          <aside class="contact-aside" aria-label="Coordonnées">
            <div class="card info-card reveal" style={{ '--i': 1 }}>
              <h2>Nous joindre</h2>
              <address>
                <span class="info-row info-row--addr">
                  <svg class="ico" aria-hidden="true">
                    <use href="#i-map-pin" />
                  </svg>
                  <span>
                    5225, rue St-Joseph
                    <br />
                    Trois-Rivières (Québec)&nbsp; G8Z 4M5
                  </span>
                </span>
                <a class="info-row" href="tel:+18193865603">
                  <svg class="ico" aria-hidden="true">
                    <use href="#i-phone" />
                  </svg>
                  819 386-5603
                </a>
                <a class="info-row" href="mailto:info@tg3r.com">
                  <svg class="ico" aria-hidden="true">
                    <use href="#i-mail" />
                  </svg>
                  info@tg3r.com
                </a>
              </address>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
